<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useTyping } from "@/composables/useTyping";
import { useStore } from "vuex";
import Modals from "../modals/Modals.vue";
import DescriptionText from "./DescriptionText.vue";
import MobileBaseBar from "./generate/MobileBaseBar.vue";
import ProfileButtonBar from "./ProfileButtonBar.vue";
import TabHolder from "../tabs/TabHolder.vue";
import GenerateBar from "./generate/GenerateBar.vue";
import StartBar from "./StartBar.vue";
import InfoPage from "./info/InfoPage.vue";
import MyMakes from "./makes/MyMakes.vue";
import ObserverWrapper from "../ObserverWrapper.vue";
import GenerateButtons from "../tabs/result/GenerateButtons.vue";
import Footer from "./Footer.vue";

const store = useStore();

const isOpen = computed(() => store.state.drawer.isOpen);
const openPurpose = computed(() => store.state.drawer.openPurpose);

const drawer = ref(null);
const generateBar = ref(null);
const showFixedGenerateBar = ref(false);
const showGenerateButtons = ref(false);

const generateBarNewPrompt = () => {
  generateBar.value.newPrompt();
};

// On phones and tablets the page slides up to just under the header, however tall the
// hero above it turned out on that screen (computers keep their fixed distances)
const isMobile = computed(() => store.state.isMobile);
const showResult = computed(() => store.state.result.showResult);

// The prompt pinned to the top steps aside while typing somewhere else on a phone
const { typing, focused } = useTyping();
const pinnedPrompt = ref(null);
const hidePinnedPrompt = computed(
  () =>
    isMobile.value &&
    typing.value &&
    !pinnedPrompt.value?.contains(focused.value)
);

const sheet = ref(null);
const white = ref(null);
const lift = ref(0);
const whiteTop = ref(0);
// Where the sheet stops once a make starts: just under the logo. On computers the
// sheet starts with the tags above its white panel, so it stops higher for the
// panel to land under the logo too.
const headerSpace = () =>
  window.innerWidth >= 1024 ? 34 : window.innerWidth >= 768 ? 96 : 56;
// Distance from the top of the page, ignoring the slide's transform
const pageTop = (el) => {
  let top = 0;
  for (let node = el; node; node = node.offsetParent) top += node.offsetTop;
  return top;
};
// On phones and tablets the hero grows to fill the screen above the landing's own
// content (the start box, examples and footer), so that sits together at the bottom
// in reach of a thumb, with no empty sheet below it. The hero reads the height from
// --landing-sheet.
const landing = ref(null);
const landingHeight = () => {
  const shown = [...landing.value.children].filter((el) => el.offsetParent);
  if (!shown.length) return 0;
  const first = shown[0];
  const last = shown[shown.length - 1];
  const top =
    first.getBoundingClientRect().top -
    parseFloat(getComputedStyle(first).marginTop);
  const bottom =
    last.getBoundingClientRect().bottom +
    parseFloat(getComputedStyle(last).marginBottom);
  // The sheet's border, top and bottom
  return Math.ceil(bottom - top) + 2;
};
const measureLanding = () => {
  const root = document.documentElement.style;
  const before = root.getPropertyValue("--landing-sheet");
  if (!isMobile.value) root.removeProperty("--landing-sheet");
  else if (landing.value)
    root.setProperty("--landing-sheet", `${landingHeight()}px`);
  // The hero (and the genie in it) has just changed size: tell the rings now, so they
  // move with it in the same frame rather than one behind
  if (root.getPropertyValue("--landing-sheet") !== before)
    window.dispatchEvent(new Event("hero-resized"));
};
const measure = () => {
  measureLanding();
  if (sheet.value)
    lift.value = Math.max(0, sheet.value.offsetTop - headerSpace());
  if (white.value) whiteTop.value = pageTop(white.value);
};
// The landing's content changes size as fonts load and the examples wrap
let landingObserver;
watch(landing, (el) => {
  landingObserver?.disconnect();
  if (!el || !window.ResizeObserver) return;
  landingObserver = new ResizeObserver(measure);
  [...el.children].forEach((child) => landingObserver.observe(child));
});
// Before a make starts, the sheet reaches the bottom of the screen however tall the hero
// is. Heights here are the screen with the browser's address bar showing (100svh),
// like the dark page behind, so the start page fits exactly and doesn't scroll. (A
// height that followed the bar, 100dvh, would grow and shrink as it slid in and out.)
const whiteStyle = computed(() =>
  !isOpen.value && whiteTop.value
    ? { minHeight: `calc(100svh - ${whiteTop.value}px)` }
    : undefined
);
const sheetStyle = computed(() =>
  isOpen.value && lift.value
    ? { transform: `translateY(-${lift.value}px)` }
    : undefined
);
let heroObserver;
onMounted(() => {
  measure();
  const hero = document.getElementById("hero");
  if (hero && window.ResizeObserver) {
    heroObserver = new ResizeObserver(measure);
    heroObserver.observe(hero);
  }
  window.addEventListener("resize", measure);
});
onUnmounted(() => {
  heroObserver?.disconnect();
  landingObserver?.disconnect();
  window.removeEventListener("resize", measure);
});
</script>

<template>
  <Modals />
  <ProfileButtonBar />
  <section ref="drawer">
    <div
      ref="sheet"
      :style="sheetStyle"
      :class="
        isOpen
          ? 'xm:-translate-y-[29rem] lg:-translate-y-[33rem] md:-translate-y-[32rem] sm:-translate-y-[30rem] xs:-translate-y-[30rem] -translate-y-[34rem]'
          : 'translate-y-0'
      "
      class="absolute z-20 flex flex-col items-end w-full transition duration-700 ease-in-out delay-500"
    >
      <div class="relative hidden float-right lg:block">
        <img
          :class="isOpen ? '-mb-[1px]' : ''"
          class="h-14 -z-10 relative"
          src="/tags.svg"
          alt="tags"
        />
      </div>
      <div
        ref="white"
        class="transition-all duration-[2000ms] w-full px-4 bg-white border border-transparent md:px-16 rounded-t-2xl lg:rounded-tr-none lg:rounded-tl-2xl"
        :class="
          isOpen ? 'min-h-screen' : 'flex flex-col min-h-[60vh] lg:min-h-[42vh]'
        "
        :style="whiteStyle"
      >
        <div v-if="!isOpen" ref="landing" class="flex flex-1 flex-col">
          <DescriptionText
            class="hidden text-right lg:block -mt-8"
            location="below"
          />
          <StartBar />
          <Footer inline class="md:hidden" />
        </div>
        <div v-else-if="openPurpose === 'createMake'">
          <ObserverWrapper @hidden="showFixedGenerateBar = $event">
            <GenerateBar ref="generateBar" />
          </ObserverWrapper>
          <TabHolder @showGenerateButtons="showGenerateButtons = $event" />
        </div>
        <MyMakes v-else-if="openPurpose === 'makes'" />
        <InfoPage v-else-if="openPurpose === 'setInfo'" />
      </div>
    </div>
    <div
      v-if="
        isOpen &&
        openPurpose === 'createMake' &&
        showFixedGenerateBar &&
        !hidePinnedPrompt &&
        !(isMobile && showResult)
      "
      ref="pinnedPrompt"
      class="fixed top-0 z-25 w-full bg-white px-4 pb-1 shadow-[0_4px_16px_rgba(35,31,66,0.06)] md:px-16 lg:-mt-8 lg:pb-0 lg:shadow-none"
    >
      <GenerateBar :hide-unnecessary="true" />
    </div>
    <div
      v-if="showGenerateButtons"
      class="w-full bg-white fixed top-0 z-25 px-4 -mt-3 border border-b-primary pr-16"
    >
      <GenerateButtons compact />
    </div>
  </section>
  <MobileBaseBar @newPrompt="generateBarNewPrompt" />
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
